import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import i18n from '../i18n/i18n';
import {
  TruckIcon,
  MapPinIcon,
  UserIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  PlusIcon,
  XMarkIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { clientsService } from '../services/logistics/clients';
import { shipmentsService } from '../services/logistics/shipments';
import { driversService } from '../services/logistics/drivers';
import { vehiclesService } from '../services/logistics/vehicles';
import { truckTypesService } from '../services/logistics/truck-types';
import { serviceTypesService } from '../services/logistics/service-types';
import { shipmentCostingsService } from '../services/logistics/shipment-costings';
import { notificationsService } from '../services/system/notifications';
import { externalDriversService } from '../services/logistics/external-drivers';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ServiceTypesModal from '../components/logistics/ServiceTypesModal';
import ServiceRequesterCombobox from '../components/logistics/ServiceRequesterCombobox';
import DriverSelect from '../components/logistics/DriverSelect';
import DateTimeField from '../components/ui/DateTimeField';
import { citiesGrouped } from '../data/saudiCities';

// Old DateTimeInput component removed - now using MUI X DateTimeField

// Stops Section Component - Simple list without drag and drop
const StopsSection = React.memo(({
  routeCity,
  removeStop,
  updateStop,
  t
}) => {
  return (
    <div className="mt-4 pl-4 border-l-4 border-blue-200 bg-gray-50 rounded-r-lg py-3">
      <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
        <span>{t('shipments.stops', 'المحطات')} ({routeCity.stops.length})</span>
      </div>
      <div className="space-y-2">
        {routeCity.stops.map((stop, stopIndex) => (
          <SimpleStop
            key={stop.id}
            stop={stop}
            stopIndex={stopIndex}
            routeCityId={routeCity.id}
            updateStop={updateStop}
            removeStop={removeStop}
            t={t}
          />
        ))}
      </div>
    </div>
  );
});
StopsSection.displayName = 'StopsSection';

// Simple City Component - No drag and drop functionality
const SimpleCity = React.memo(({
  routeCity,
  cityIndex,
  updateCity,
  removeCity,
  addStop,
  removeStop,
  updateStop,
  t,
  citiesGrouped,
  i18n,
  canRemoveCity
}) => {
  return (
    <div className="border-2 border-gray-200 rounded-xl p-4 bg-white transition-all duration-200 hover:shadow-md hover:border-gray-300">
      {/* City Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm">
            {cityIndex + 1}
          </div>
          <span className="text-base font-semibold text-gray-800">
            {t('shipments.city', 'المدينة')} {cityIndex + 1}
          </span>
        </div>
        {canRemoveCity && (
          <button
            type="button"
            onClick={() => removeCity(routeCity.id)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
            title={t('common.remove', 'إزالة')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* City Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('shipments.select_city', 'اختر المدينة')} *
          </label>
          <select
            value={routeCity.city}
            onChange={(e) => updateCity(routeCity.id, 'city', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            required
          >
            <option value="">{t('shipments.select_city', 'اختر المدينة')}</option>
            <optgroup label={t('shipments.major_cities', 'المدن الرئيسية')}>
              {citiesGrouped.major.map(city => (
                <option key={city.value} value={city.value}>
                  {city.label[i18n.language] || city.label.en}
                </option>
              ))}
            </optgroup>
            <optgroup label={t('shipments.other_cities', 'مدن أخرى')}>
              {citiesGrouped.others.map(city => (
                <option key={city.value} value={city.value}>
                  {city.label[i18n.language] || city.label.en}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('shipments.site', 'الموقع')}
          </label>
          <input
            type="text"
            value={routeCity.site}
            onChange={(e) => updateCity(routeCity.id, 'site', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder={t('shipments.site', 'الموقع')}
          />
        </div>
      </div>

      {/* Stops Section */}
      {routeCity.stops.length > 0 && (
        <StopsSection
          routeCity={routeCity}
          removeStop={removeStop}
          updateStop={updateStop}
          t={t}
        />
      )}

      {/* Add Stop Button - Compact & Integrated */}
      <div className="mt-2">
        <button
          type="button"
          onClick={() => addStop(routeCity.id)}
          className="flex items-center space-x-1 rtl:space-x-reverse text-gray-600 hover:text-blue-600 hover:bg-gray-100 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          <span>{t('shipments.add_stop', 'إضافة محطة')}</span>
        </button>
      </div>
    </div>
  );
});
SimpleCity.displayName = 'SimpleCity';

// Simple Stop Component - No drag and drop functionality
const SimpleStop = React.memo(({
  stop,
  stopIndex,
  routeCityId,
  updateStop,
  removeStop,
  t
}) => {
  return (
    <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center space-x-3 rtl:space-x-reverse transition-all duration-200 hover:shadow-sm hover:border-gray-300">
      <div className="flex items-center space-x-3 rtl:space-x-reverse flex-1">
        <div className="w-6 h-6 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
          {stopIndex + 1}
        </div>
        <input
          type="text"
          value={stop.name}
          onChange={(e) => updateStop(routeCityId, stop.id, e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          placeholder={t('shipments.stop_name', 'اسم المحطة')}
        />
      </div>
      <button
        type="button"
        onClick={() => removeStop(routeCityId, stop.id)}
        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
        title={t('common.remove', 'إزالة')}
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
});
SimpleStop.displayName = 'SimpleStop';

// Quick Item Form Component
const QuickItemForm = ({ item, onSave, onCancel, t }) => {
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    price: 0,
    isVatIncluded: false
  });

  React.useEffect(() => {
    if (item) {
      setFormData(item);
    } else {
      setFormData({
        name: '',
        nameEn: '',
        price: 0,
        isVatIncluded: false
      });
    }
  }, [item]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.nameEn.trim()) {
      return;
    }
    onSave(formData);
    setFormData({
      name: '',
      nameEn: '',
      price: 0,
      isVatIncluded: false
    });
  };

  const convertArabicToEnglish = (str) => {
    const arabicNumbers = '٠١٢٣٤٥٦٧٨٩';
    const englishNumbers = '0123456789';
    return str.replace(/[٠-٩]/g, (match) => englishNumbers[arabicNumbers.indexOf(match)]);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-blue-50 rounded-lg">
      <h4 className="font-medium text-gray-900">
        {item ? t('itemized_costs.edit_quick_item', 'Edit Quick Item') : t('itemized_costs.add_quick_item', 'Add Quick Item')}
      </h4>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('itemized_costs.arabic_name', 'Arabic Name')} *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="مبيت"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('itemized_costs.english_name', 'English Name')} *
          </label>
          <input
            type="text"
            value={formData.nameEn}
            onChange={(e) => setFormData(prev => ({ ...prev, nameEn: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Overnight Stay"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('itemized_costs.default_price', 'Default Price')} (SAR)
          </label>
          <input
            type="text"
            value={formData.price === 0 ? '' : formData.price}
            onChange={(e) => {
              const convertedValue = convertArabicToEnglish(e.target.value);
              setFormData(prev => ({ ...prev, price: parseFloat(convertedValue) || 0 }));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0.00"
            onFocus={(e) => e.target.select()}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('itemized_costs.vat_setting', 'VAT Setting')}
          </label>
          <div className="flex items-center space-x-4 mt-2">
            <label className="flex items-center">
              <input
                type="radio"
                name="isVatIncluded"
                checked={!formData.isVatIncluded}
                onChange={() => setFormData(prev => ({ ...prev, isVatIncluded: false }))}
                className="mr-2"
              />
              {t('itemized_costs.vat_excluded', 'VAT Excluded')}
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="isVatIncluded"
                checked={formData.isVatIncluded}
                onChange={() => setFormData(prev => ({ ...prev, isVatIncluded: true }))}
                className="mr-2"
              />
              {t('itemized_costs.vat_included', 'VAT Included')}
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {t('common.cancel', t('common.cancel'))}
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700"
        >
          {item ? t('common.update', 'Update') : t('common.add', t('common.add'))}
        </button>
      </div>
    </form>
  );
};

const CreateShipment = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentCompany, user } = useAuth();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [truckTypes, setTruckTypes] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [driverBusyMap, setDriverBusyMap] = useState(new Map());
  const [externalDriversList, setExternalDriversList] = useState([]);
  const [externalDriverUsage, setExternalDriverUsage] = useState(new Map());
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [showServiceTypesModal, setShowServiceTypesModal] = useState(false);
  const [showQuickClientModal, setShowQuickClientModal] = useState(false);
  const [quickClientLoading, setQuickClientLoading] = useState(false);
  const [quickClientData, setQuickClientData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: ''
  });

  // Quick add items management
  const [showQuickItemsModal, setShowQuickItemsModal] = useState(false);
  const [quickItems, setQuickItems] = useState([
    { id: 1, name: 'مبيت', nameEn: 'Overnight Stay', price: 0, isVatIncluded: false },
    { id: 2, name: 'عمالة التحميل', nameEn: 'Loading Labor', price: 0, isVatIncluded: false },
    { id: 3, name: 'عمالة التفريغ', nameEn: 'Unloading Labor', price: 0, isVatIncluded: false },
    { id: 4, name: 'رسوم المحطة', nameEn: 'Stop Fee', price: 0, isVatIncluded: false }
  ]);
  const [editingQuickItem, setEditingQuickItem] = useState(null);

  // Stable ID generators using refs
  const nextCityId = React.useRef(1000);
  const nextStopId = React.useRef(2000);
  const nextCostItemId = React.useRef(3000);

  // Get current date and time for defaults
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Format date for display as DD/MM/YYYY
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Format datetime for display as DD/MM/YYYY HH:MM
  const formatDateTimeForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for input value
  const convertDisplayDateToInputValue = (displayDate) => {
    if (!displayDate) return '';
    const parts = displayDate.split('/');
    if (parts.length !== 3) return '';
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  // Helper function to get truck type name
  const getTruckTypeName = (truckTypeValue) => {
    if (!truckTypeValue) return t('common.not_specified', 'Not specified');

    const truckType = truckTypes.find(tt => tt.value === truckTypeValue);
    if (truckType && truckType.name) {
      return truckType.name[i18n.language] || truckType.name.en;
    }

    return truckTypeValue;
  };

  // Utility function to ensure time is always in HH:MM format, robust against potential format issues
  const formatTimeToHHMM = (timeString) => {
    if (!timeString || typeof timeString !== 'string') {
      // Fallback to current time if input is invalid
      const now = new Date();
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }

    // Split by colon
    const parts = timeString.split(':');

    // Expecting at least two parts for HH:MM
    if (parts.length < 2) {
      // Fallback if splitting by ':' doesn't yield enough parts
      const now = new Date();
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }

    let hours, minutes;

    // Attempt to parse the first two parts as integers
    const potentialHours = parseInt(parts[0], 10);
    const potentialMinutes = parseInt(parts[1], 10);

    // Validate if they are numbers
    if (isNaN(potentialHours) || isNaN(potentialMinutes)) {
      // Fallback if parsing fails
      const now = new Date();
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }

    // Assign parsed values
    hours = potentialHours;
    minutes = potentialMinutes;

    // Ensure two-digit formatting (this corrects the display order)
    // This step ensures that even if parsing was correct, the final string is HH:MM
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const getTimeFromDateTime = (dateTimeString) => {
    if (!dateTimeString || typeof dateTimeString !== 'string') {
      const now = new Date();
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }

    if (dateTimeString.includes('T')) {
      const timePart = dateTimeString.split('T')[1];
      if (timePart) {
        // Use the robust formatter
        return formatTimeToHHMM(timePart);
      }
    }

    // Fallback to current time
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  const [formData, setFormData] = useState({
    clientId: '',
    requesterName: '',
    pickupDate: getCurrentDateTime(),
    deliveryDate: '',
    driverId: '',
    vehicleId: '',
    truckType: '', // For external drivers
    cargoDescription: '',
    cargoWeight: '',
    cargoValue: '',
    notes: '',
    priority: 'normal',
    serviceType: 'dry',
    vatRate: 15,
    shipmentPrice: '',
    isVatIncluded: false,
    // Backhaul fields
    showBackhaul: false,
    backhaulPrice: '',
    backhaulDescription: '',
    // External driver fields
    useExternalDriver: false,
    externalDriverName: '',
    externalDriverPhone: '',
    externalDriverId: '',
    // Route system - Start with two cities with stable IDs
    routeCities: [
      { id: nextCityId.current++, city: '', site: '', stops: [] },
      { id: nextCityId.current++, city: '', site: '', stops: [] }
    ],
    // Itemized costs
    itemizedCosts: []
  });



  // Route management functions
  const addCity = React.useCallback(() => {
    setFormData(prev => ({
      ...prev,
      routeCities: [
        ...prev.routeCities,
        { id: nextCityId.current++, city: '', site: '', stops: [] }
      ]
    }));
  }, []);

  const removeCity = React.useCallback((cityId) => {
    setFormData(prev => {
      const city = prev.routeCities.find(c => c.id === cityId);

      // Check if city has stops and confirm removal
      if (city?.stops.length > 0) {
        if (!window.confirm(t('shipments.confirm_remove_city_with_stops', 'This city has stops. Are you sure you want to remove it?'))) {
          return prev;
        }
      }

      if (prev.routeCities.length <= 2) {
        toast.error(t('validation.min_cities', 'Route must have at least 2 cities'));
        return prev;
      }

      return {
        ...prev,
        routeCities: prev.routeCities.filter(city => city.id !== cityId)
      };
    });
  }, [t]); // Added t to dependencies

  const updateCity = React.useCallback((cityId, field, value) => {
    setFormData(prev => ({
      ...prev,
      routeCities: prev.routeCities.map(city =>
        city.id === cityId ? { ...city, [field]: value } : city
      )
    }));
  }, []);

  const addStop = React.useCallback((cityId) => {
    setFormData(prev => ({
      ...prev,
      routeCities: prev.routeCities.map(city =>
        city.id === cityId
          ? { ...city, stops: [...city.stops, { id: nextStopId.current++, name: '' }] }
          : city
      )
    }));
  }, []);

  const removeStop = React.useCallback((cityId, stopId) => {
    setFormData(prev => ({
      ...prev,
      routeCities: prev.routeCities.map(city =>
        city.id === cityId
          ? { ...city, stops: city.stops.filter(stop => stop.id !== stopId) }
          : city
      )
    }));
  }, []);

  const updateStop = React.useCallback((cityId, stopId, value) => {
    setFormData(prev => ({
      ...prev,
      routeCities: prev.routeCities.map(city =>
        city.id === cityId
          ? {
            ...city,
            stops: city.stops.map(stop =>
              stop.id === stopId ? { ...stop, name: value } : stop
            )
          }
          : city
      )
    }));
  }, []);

  // Itemized cost management functions
  const addCostItem = React.useCallback(() => {
    setFormData(prev => ({
      ...prev,
      itemizedCosts: [
        ...prev.itemizedCosts,
        {
          id: nextCostItemId.current++,
          description: '',
          quantity: 1,
          unitPrice: 0,
          total: 0
        }
      ]
    }));
  }, []);

  const removeCostItem = React.useCallback((itemId) => {
    setFormData(prev => ({
      ...prev,
      itemizedCosts: prev.itemizedCosts.filter(item => item.id !== itemId)
    }));
  }, []);

  const updateCostItem = React.useCallback((itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      itemizedCosts: prev.itemizedCosts.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          // Auto-calculate total when quantity or unitPrice changes
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.total = (parseFloat(updatedItem.quantity) || 0) * (parseFloat(updatedItem.unitPrice) || 0);
          }
          return updatedItem;
        }
        return item;
      })
    }));
  }, []);

  // Calculate itemized costs total
  const calculateItemizedTotal = React.useCallback(() => {
    return formData.itemizedCosts.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  }, [formData.itemizedCosts]);

  // Utility function to convert Arabic numerals to English
  const convertArabicToEnglish = (str) => {
    const arabicNumbers = '٠١٢٣٤٥٦٧٨٩';
    const englishNumbers = '0123456789';
    return str.replace(/[٠-٩]/g, (match) => englishNumbers[arabicNumbers.indexOf(match)]);
  };

  // Toggle backhaul visibility
  const toggleBackhaul = () => {
    setFormData(prev => ({
      ...prev,
      showBackhaul: !prev.showBackhaul,
      // Clear backhaul data when hiding
      backhaulPrice: prev.showBackhaul ? '' : prev.backhaulPrice,
      backhaulDescription: prev.showBackhaul ? '' : prev.backhaulDescription
    }));
  };

  // Handle number input with Arabic to English conversion
  const handleNumberInput = (value, callback) => {
    const convertedValue = convertArabicToEnglish(value);
    callback(convertedValue);
  };

  // Quick items management functions
  const saveQuickItem = (item) => {
    if (editingQuickItem) {
      setQuickItems(prev => prev.map(qi => qi.id === item.id ? item : qi));
    } else {
      const newItem = { ...item, id: Date.now() };
      setQuickItems(prev => [...prev, newItem]);
    }
    setEditingQuickItem(null);
  };

  const deleteQuickItem = (id) => {
    setQuickItems(prev => prev.filter(qi => qi.id !== id));
  };

  // Fetch all form data in parallel
  useEffect(() => {
    if (!currentCompany?.id) {
      setLoadingClients(false);
      setLoadingDrivers(false);
      setLoadingVehicles(false);
      return;
    }

    const companyId = currentCompany.id;

    const fetchFormData = async () => {
      try {
        const [clientsData, driversData, vehiclesData, truckTypesData, serviceTypesData] = await Promise.all([
          clientsService.getClients(companyId),
          driversService.getDrivers(companyId),
          vehiclesService.getVehicles(companyId),
          truckTypesService.getTruckTypes(companyId),
          serviceTypesService.getServiceTypes(companyId),
        ]);
        setClients(clientsData);
        setDrivers(driversData);
        setVehicles(vehiclesData);
        setTruckTypes(truckTypesData);
        setServiceTypes(serviceTypesData);

        // Build busy map — non-critical, don't let failure block the form
        try {
          const shipmentsData = await shipmentsService.getShipments(companyId);
          const busyMap = new Map();
          const usage = new Map();
          shipmentsData.forEach(s => {
            if (s.status !== 'delivered' && s.status !== 'canceled') {
              if (!(isEditMode && s.id === id) && s.driverId && !s.isExternalDriver) {
                busyMap.set(s.driverId, { shipmentStatus: s.status });
              }
            }
            if (s.isExternalDriver && s.externalDriverId) {
              const dateMs =
                (s.pickupDate ? new Date(s.pickupDate).getTime() : 0) ||
                (s.shipmentDate ? new Date(s.shipmentDate).getTime() : 0) ||
                (s.createdAt?.toDate ? s.createdAt.toDate().getTime() : 0) ||
                (s.createdAt?.seconds ? s.createdAt.seconds * 1000 : 0) ||
                0;
              const prev = usage.get(s.externalDriverId) || { count: 0, lastUsedMs: 0 };
              usage.set(s.externalDriverId, {
                count: prev.count + 1,
                lastUsedMs: Math.max(prev.lastUsedMs, dateMs)
              });
            }
          });
          setDriverBusyMap(busyMap);
          setExternalDriverUsage(usage);
        } catch {
          // Busy map unavailable — driver list still works without availability info
        }

        // External drivers — non-critical, populate dropdown for quick selection
        try {
          const externals = await externalDriversService.getExternalDrivers(companyId);
          setExternalDriversList(externals);
        } catch {
          // Dropdown empty — phone search/registration still works
        }
      } catch (error) {
        console.error('Error fetching form data:', error);
        toast.error('Failed to load form data');
      } finally {
        setLoadingClients(false);
        setLoadingDrivers(false);
        setLoadingVehicles(false);
      }
    };

    fetchFormData();
  }, [currentCompany?.id]);

  // Load shipment data if in edit mode
  useEffect(() => {
    const loadShipmentData = () => {
      if (!isEditMode || !id) return;

      setLoading(true);
      shipmentsService.getShipment(id, currentCompany?.id).then(shipmentData => {
        let loadedCities = [];
        if (shipmentData.routeCities?.length > 0) {
          loadedCities = shipmentData.routeCities.map(city => ({
            ...city,
            id: (city.id !== undefined && city.id !== null) ? city.id : nextCityId.current++,
            stops: (city.stops || []).map(stop =>
              (stop.id !== undefined && stop.id !== null) ? stop : { ...stop, id: nextStopId.current++ }
            )
          }));
        } else {
          loadedCities = [
            { id: nextCityId.current++, city: shipmentData.originCity || '', site: '', stops: [] },
            { id: nextCityId.current++, city: shipmentData.destinationCity || '', site: '', stops: [] }
          ];
        }

        setFormData(prev => ({
          ...prev,
          clientId: shipmentData.clientId || '',
          requesterName: shipmentData.requesterName || '',
          routeCities: loadedCities,
          pickupDate: shipmentData.pickupDate ? new Date(shipmentData.pickupDate).toISOString().slice(0, 16) : getCurrentDateTime(),
          deliveryDate: shipmentData.expectedDeliveryDate ? new Date(shipmentData.expectedDeliveryDate).toISOString().slice(0, 16) : '',
          driverId: shipmentData.driverId || '',
          vehicleId: shipmentData.vehicleId || '',
          // External driver fields
          useExternalDriver: shipmentData.isExternalDriver || false,
          externalDriverName: shipmentData.isExternalDriver ? shipmentData.driverName || '' : '',
          externalDriverPhone: shipmentData.externalDriverPhone || '',
          externalDriverId: shipmentData.externalDriverId || '',
          cargoDescription: shipmentData.cargoDescription || '',
          cargoWeight: shipmentData.cargoWeight || '',
          cargoValue: shipmentData.totalValue || '',
          notes: shipmentData.notes || '',
          priority: shipmentData.priority || 'normal',
          serviceType: shipmentData.serviceType || 'dry',
          vatRate: shipmentData.vatRate || 15,
          shipmentPrice: shipmentData.cost || '',
          isVatIncluded: shipmentData.isVatIncluded || false,
          // Load backhaul data if available
          showBackhaul: Boolean(shipmentData.backhaulPrice || shipmentData.backhaulDescription),
          backhaulPrice: shipmentData.backhaulPrice || '',
          backhaulDescription: shipmentData.backhaulDescription || '',
          // Load itemized costs if available
          itemizedCosts: (shipmentData.itemizedCosts || []).map(item => ({
            ...item,
            id: item.id !== undefined ? item.id : nextCostItemId.current++
          }))
        }));
      }).catch(() => {
        toast.error('Failed to load shipment data');
        navigate('/shipments');
      }).finally(() => setLoading(false));
    };

    loadShipmentData();
  }, [isEditMode, id, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If driver is being selected, auto-select their favorite vehicle
    if (name === 'driverId' && value) {
      const selectedDriver = drivers.find(driver => driver.id === value);
      if (selectedDriver?.favoriteVehicleId) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          vehicleId: selectedDriver.favoriteVehicleId
        }));
        return;
      }
    }

    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle external driver phone change with auto-fill
  const handleExternalDriverPhoneChange = async (e) => {
    const phone = e.target.value;
    setFormData(prev => ({ ...prev, externalDriverPhone: phone }));

    // If phone number is complete (10+ digits), try to find existing driver
    if (phone.length >= 10 && currentCompany?.id) {
      try {
        const existingDriver = await externalDriversService.findDriverByPhone(currentCompany.id, phone);
        if (existingDriver) {
          setFormData(prev => ({
            ...prev,
            externalDriverName: existingDriver.name,
            externalDriverId: existingDriver.id
          }));
          toast.success(t('drivers.external_driver_updated', 'Driver information auto-filled'));
        } else {
          setFormData(prev => ({ ...prev, externalDriverId: '' }));
        }
      } catch (error) {
        console.error('Error finding external driver:', error);
      }
    }
  };

  // Handle external driver checkbox change
  const handleExternalDriverToggle = (e) => {
    const useExternal = e.target.checked;
    setFormData(prev => ({
      ...prev,
      useExternalDriver: useExternal,
      // Clear regular driver selection if using external
      driverId: useExternal ? '' : prev.driverId,
      // Clear vehicle field when switching, but preserve truck type for external drivers
      vehicleId: useExternal ? '' : prev.vehicleId,
      // Keep truck type for external drivers, clear only for internal vehicles
      truckType: useExternal ? prev.truckType : '',
      // Clear external driver fields if not using external
      externalDriverName: useExternal ? prev.externalDriverName : '',
      externalDriverPhone: useExternal ? prev.externalDriverPhone : '',
      externalDriverId: useExternal ? prev.externalDriverId : ''
    }));
  };

  const handleClientSelection = (e) => {
    const value = e.target.value;
    if (value === 'add_new_client') {
      setShowQuickClientModal(true);
    } else {
      handleInputChange(e);
      // Clear requester name when client changes
      setFormData(prev => ({ ...prev, requesterName: '' }));
    }
  };

  const handleQuickClientInputChange = (e) => {
    const { name, value } = e.target;
    setQuickClientData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateClientId = () => {
    const timestamp = Date.now().toString().slice(-6);
    const prefix = currentCompany?.name?.substring(0, 2).toUpperCase() || 'CL';
    return `${prefix}${timestamp}`;
  };

  const handleQuickClientSubmit = async (e) => {
    e.preventDefault();

    if (!quickClientData.companyName.trim()) {
      toast.error('Company name is required');
      return;
    }

    setQuickClientLoading(true);

    try {
      const clientData = {
        companyId: currentCompany.id,
        clientId: generateClientId(),
        companyName: quickClientData.companyName.trim(),
        contactPerson: quickClientData.contactPerson.trim() || t('common.not_available', 'N/A'),
        email: quickClientData.email.trim() || '',
        phone: quickClientData.phone.trim() || '',
        address: quickClientData.address.trim() || '',
        city: '',
        country: 'saudi',
        notes: 'Quick created - complete information in clients section',
        lastShipment: null,
        status: 'active',
        totalShipments: 0,
        totalRevenue: 0,
        isQuickCreated: true
      };

      const newClientId = await clientsService.addClient(clientData);

      // Add to local clients list
      const newClient = { id: newClientId, ...clientData };
      setClients(prev => [newClient, ...prev]);

      // Select the new client
      setFormData(prev => ({
        ...prev,
        clientId: newClientId
      }));

      // Reset and close modal
      setQuickClientData({
        companyName: '',
        contactPerson: '',
        phone: '',
        email: '',
        address: ''
      });
      setShowQuickClientModal(false);

      toast.success('Client added successfully! You can complete the information later in the Clients section.');
    } catch (error) {
      console.error('Error creating quick client:', error);
      toast.error('Failed to create client');
    } finally {
      setQuickClientLoading(false);
    }
  };

  const validateForm = () => {
    // Only client and pickup date are required — driver, vehicle, price can be set later.
    const basicRequired = ['clientId', 'pickupDate'];
    const missing = basicRequired.filter(field => !formData[field]);

    if (missing.length > 0) {
      toast.error(t('validation.required_fields', 'Please fill in all required fields'));
      return false;
    }

    // Route validation - Advanced route only
    const validCities = formData.routeCities.filter(city => city.city);
    if (validCities.length < 2) {
      toast.error(t('validation.min_cities', 'Please select at least 2 cities for the route'));
      return false;
    }

    if (formData.deliveryDate && new Date(formData.deliveryDate) <= new Date(formData.pickupDate)) {
      toast.error(t('validation.delivery_after_pickup', 'Delivery date must be after pickup date'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!currentCompany?.id) {
      toast.error(t('common.company_not_loaded', 'Company information is not available.'));
      return;
    }

    setLoading(true);

    try {
      const selectedClient = clients.find(c => c.id === formData.clientId);
      let selectedDriver = null;
      let externalDriverId = null;

      // Handle driver selection based on type
      if (formData.useExternalDriver) {
        // Create or update external driver
        externalDriverId = await externalDriversService.addOrUpdateExternalDriver(
          currentCompany.id,
          {
            name: formData.externalDriverName.trim(),
            phone: formData.externalDriverPhone.trim()
          }
        );
      } else {
        selectedDriver = drivers.find(d => d.id === parseInt(formData.driverId));
      }

      const selectedVehicle = vehicles.find(v => v.id === parseInt(formData.vehicleId));

      // Get route information from routeCities
      const validCities = formData.routeCities.filter(city => city.city);
      const originCityValue = validCities[0]?.city;
      const destinationCityValue = validCities[validCities.length - 1]?.city;

      const originCity = citiesGrouped.major.find(c => c.value === originCityValue) ||
        citiesGrouped.others.find(c => c.value === originCityValue);
      const destinationCity = citiesGrouped.major.find(c => c.value === destinationCityValue) ||
        citiesGrouped.others.find(c => c.value === destinationCityValue);

      // Determine shipment type based on route
      const shipmentType = originCityValue === destinationCityValue ? 'local' : 'outbound';

      const shipmentData = {
        companyId: currentCompany.id,
        clientId: formData.clientId,
        clientName: selectedClient?.companyName || 'Unknown Client',
        requesterName: formData.requesterName,
        origin: originCity?.label?.en || originCityValue,
        destination: destinationCity?.label?.en || destinationCityValue,
        originCity: originCityValue,
        destinationCity: destinationCityValue,
        shipmentType: shipmentType,
        routeCities: formData.routeCities,
        pickupDate: formData.pickupDate,
        expectedDeliveryDate: formData.deliveryDate || null,
        // Driver information based on type
        driverId: formData.useExternalDriver ? null : (formData.driverId || null),
        driverName: formData.useExternalDriver
          ? (formData.externalDriverName?.trim() || null)
          : (selectedDriver?.name || null),
        isExternalDriver: formData.useExternalDriver,
        externalDriverId: externalDriverId,
        externalDriverPhone: formData.useExternalDriver ? (formData.externalDriverPhone || null) : null,
        paymentStatus: formData.useExternalDriver ? 'not_paid' : null, // Default payment status for external drivers
        // Vehicle information based on type
        vehicleId: formData.useExternalDriver ? null : (formData.vehicleId || null),
        vehiclePlate: formData.useExternalDriver ? null : (selectedVehicle?.plateNumber || null),
        vehicleInfo: formData.useExternalDriver
          ? (formData.truckType ? `External Driver - ${formData.truckType}` : null)
          : (selectedVehicle ? `${selectedVehicle.plateNumber} - ${getTruckTypeName(selectedVehicle.truckType || selectedVehicle.type)}` : null),
        truckType: formData.useExternalDriver ? (formData.truckType || null) : (selectedVehicle?.truckType || selectedVehicle?.type || null),
        cargoDescription: formData.cargoDescription,
        cargoWeight: formData.cargoWeight ? parseFloat(formData.cargoWeight) || null : null,
        totalValue: formData.cargoValue ? parseFloat(formData.cargoValue) || null : null,
        notes: formData.notes,
        priority: formData.priority,
        serviceType: formData.serviceType,
        cost: parseFloat(formData.shipmentPrice) || 0,
        isVatIncluded: formData.isVatIncluded,
        vatRate: 15,
        // Backhaul data
        backhaulPrice: formData.showBackhaul ? (parseFloat(formData.backhaulPrice) || 0) : 0,
        backhaulDescription: formData.showBackhaul ? formData.backhaulDescription : '',
        itemizedCosts: formData.itemizedCosts,
        itemizedCostsTotal: calculateItemizedTotal(),
        updatedAt: new Date()
      };

      if (isEditMode) {
        // Update existing shipment
        await shipmentsService.updateShipment(id, shipmentData);
        toast.success('Shipment updated successfully!');
        navigate(`/shipments/${id}`);
      } else {
        // Create new shipment — number is generated atomically inside the
        // create transaction so failed creates don't burn a sequence number.
        shipmentData.status = 'pending';
        shipmentData.createdBy = user.uid;

        const { id: newShipmentId, trackingNumber } =
          await shipmentsService.addShipmentWithGeneratedNumber(shipmentData);
        shipmentData.trackingNumber = trackingNumber;

        // Auto-create costing record for the new shipment
        try {
          await shipmentCostingsService.autoCreateCosting({
            shipmentId: newShipmentId,
            companyId: currentCompany.id,
            createdBy: user.uid,
            driverId: formData.useExternalDriver ? null : formData.driverId // Add driver ID for commission tracking (only for internal drivers)
          });

          // Update external driver stats if applicable
          if (formData.useExternalDriver && externalDriverId) {
            await externalDriversService.updateDriverStats(externalDriverId, formData.shipmentPrice);
          }

          // Notify all accountants about the new shipment costing record
          await notificationsService.createRoleNotification(
            currentCompany.id,
            ['accounting'],
            {
              title: 'New Shipment Costing Ready',
              message: `A new shipment costing record has been created for ${trackingNumber}. Click to manage costing details.`,
              type: 'info',
              data: {
                shipmentId: newShipmentId,
                trackingNumber: trackingNumber,
                action: 'view_costing'
              }
            }
          );
        } catch (costingError) {
          console.error('Error creating costing record or notification:', costingError);
          // Don't fail the shipment creation if costing fails
          toast.error('Shipment created but failed to initialize costing record');
        }

        toast.success(`Shipment created successfully! Shipment number: ${trackingNumber}`);
        navigate('/shipments');
      }

    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} shipment:`, error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} shipment`);
    } finally {
      setLoading(false);
    }
  };

  // Debug: Check current language and translation
  console.log('Current language:', i18n.language);
  console.log('Translation test:', t('itemized_costs.itemized_costs', 'Itemized Costs'));
  console.log('Translation test 2:', t('common.currency', 'SAR'));
  console.log('Available languages:', i18n.languages);
  console.log('Current language resources:', i18n.hasResourceBundle ? i18n.hasResourceBundle(i18n.language, 'translation') : 'N/A');

  if (loadingClients) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4 rtl:space-x-reverse">
        <button
          onClick={() => navigate('/shipments')}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? t('shipments.edit_shipment', 'Edit Shipment') : t('shipments.create_shipment')}
          </h1>
          <p className="text-sm text-gray-500">
            {isEditMode ? t('shipments.edit_subtitle', 'Update shipment information') : t('shipments.create_subtitle')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client & Basic Info */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold flex items-center">
                  <UserIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                  {t('shipments.client_information')}
                </h3>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="label">{t('shipments.client')} *</label>
                  <select
                    name="clientId"
                    value={formData.clientId}
                    onChange={handleClientSelection}
                    className="input-field"
                    required
                  >
                    <option value="">{t('shipments.select_client')}</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.companyName} - {client.contactPerson}
                      </option>
                    ))}
                    <option value="add_new_client" className="text-blue-600 font-medium">
                      + Add New Client (Quick)
                    </option>
                  </select>
                  {clients.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      No clients found. Use "Add New Client" option above to create one quickly.
                    </p>
                  )}
                </div>

                {/* Service Requester Field */}
                <div>
                  <label className="label">{t('shipments.service_requester')}</label>
                  <ServiceRequesterCombobox
                    clientId={formData.clientId}
                    value={formData.requesterName}
                    onChange={(value) => setFormData(prev => ({ ...prev, requesterName: value }))}
                    disabled={!formData.clientId}
                    required={false}
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t('shipments.service_type')}</label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      {serviceTypes.map(serviceType => (
                        <option key={serviceType.value} value={serviceType.value}>
                          {serviceType.name?.[i18n.language] || serviceType.name?.en || serviceType.value}
                        </option>
                      ))}
                    </select>
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        onClick={() => setShowServiceTypesModal(true)}
                        className="btn btn-ghost btn-xs text-blue-600 hover:text-blue-800"
                        title={t('service_types.manage_service_types', 'Manage Service Types')}
                      >
                        <CogIcon className="w-4 h-4 mr-1" />
                        {t('service_types.manage_service_types', 'Manage Service Types')}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="label">{t('shipments.priority')}</label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      <option value="low">{t('shipments.priority_low')}</option>
                      <option value="normal">{t('shipments.priority_normal')}</option>
                      <option value="high">{t('shipments.priority_high')}</option>
                      <option value="urgent">{t('shipments.priority_urgent')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Route Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold flex items-center">
                  <MapPinIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                  {t('shipments.route_information')}
                </h3>
              </div>
              <div className="card-body space-y-3">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>{t('shipments.advanced_route_help', 'Advanced Route Mode')}:</strong> {t('shipments.advanced_route_desc', 'Build your route by adding cities in sequence. Each city can have a main site and multiple stops.')}
                  </p>
                </div>

                {/* Horizontal Route Cards */}
                <div className="overflow-x-auto">
                  <div className="flex space-x-3 rtl:space-x-reverse min-w-max pb-2">
                    {formData.routeCities.map((routeCity, cityIndex) => (
                      <div key={routeCity.id} className="flex items-center">
                        {/* City Card */}
                        <div className="border border-gray-200 rounded-lg p-3 bg-white transition-all hover:shadow-sm min-w-[200px] relative">
                          {/* City Dropdown */}
                          <div className="mb-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {t('shipments.city')}
                            </label>
                            <select
                              value={routeCity.city}
                              onChange={(e) => updateCity(routeCity.id, 'city', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              required
                            >
                              <option value="">{t('shipments.select_city')}</option>
                              <optgroup label={t('shipments.major_cities')}>
                                {citiesGrouped.major.map((city) => (
                                  <option key={city.value} value={city.value}>
                                    {city.label[i18n.language] || city.label.en}
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label={t('shipments.other_cities')}>
                                {citiesGrouped.others.map((city) => (
                                  <option key={city.value} value={city.value}>
                                    {city.label[i18n.language] || city.label.en}
                                  </option>
                                ))}
                              </optgroup>
                            </select>
                          </div>

                          {/* Site Input */}
                          <div className="mb-3">
                            <input
                              type="text"
                              value={routeCity.site}
                              onChange={(e) => updateCity(routeCity.id, 'site', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder={t('shipments.site')}
                            />
                          </div>

                          {/* Remove City Button (except first city) */}
                          {formData.routeCities.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeCity(routeCity.id)}
                              className={`absolute top-2 p-1 rounded text-red-500 hover:text-red-700 hover:bg-red-50 ${i18n.language === 'ar' ? 'left-2' : 'right-2'
                                }`}
                              aria-label={t('common.remove')}
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          )}

                          {/* Stops Section */}
                          {routeCity.stops.length > 0 && (
                            <div className="mb-2">
                              <div className="space-y-1">
                                {routeCity.stops.map((stop, stopIndex) => (
                                  <div
                                    key={stop.id}
                                    className="flex items-center space-x-1 rtl:space-x-reverse bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs"
                                  >
                                    <span className="w-4 h-4 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                      {stopIndex + 1}
                                    </span>
                                    <input
                                      type="text"
                                      value={stop.name}
                                      onChange={(e) => updateStop(routeCity.id, stop.id, e.target.value)}
                                      className="flex-1 border-none outline-none text-xs bg-transparent"
                                      placeholder={t('shipments.stop_name')}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeStop(routeCity.id, stop.id)}
                                      className="text-red-500 hover:text-red-700 p-0.5"
                                      aria-label={t('common.remove')}
                                    >
                                      <XMarkIcon className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Add Stop Button */}
                          <button
                            type="button"
                            onClick={() => addStop(routeCity.id)}
                            className="w-full flex items-center justify-center space-x-1 rtl:space-x-reverse text-gray-600 hover:text-blue-600 hover:bg-gray-50 px-2 py-1.5 rounded text-xs font-medium transition-colors border border-gray-200"
                          >
                            <PlusIcon className="w-3 h-3" />
                            <span>{t('shipments.add_stop')}</span>
                          </button>
                        </div>

                        {/* Arrow Separator (except last city) */}
                        {cityIndex < formData.routeCities.length - 1 && (
                          <div className="flex items-center px-2">
                            <span className="text-gray-400 text-lg">
                              {i18n.language === 'ar' ? '←' : '→'}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Add City Button */}
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={addCity}
                        className="flex items-center space-x-1 rtl:space-x-reverse text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-blue-200"
                        aria-label={t('shipments.add_city')}
                      >
                        <PlusIcon className="w-4 h-4" />
                        <span>{t('shipments.add_city')}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Route Summary */}
                {formData.routeCities.some((city) => city.city) && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200 mt-3">
                    <div className="text-xs font-semibold text-green-900 mb-2">{t('shipments.route_summary')}:</div>
                    <div className="flex flex-wrap gap-1.5">
                      {formData.routeCities.map((city, index) => {
                        if (!city.city) return null;
                        const cityLabel =
                          citiesGrouped.major.find((c) => c.value === city.city)?.label[i18n.language] ||
                          citiesGrouped.others.find((c) => c.value === city.city)?.label[i18n.language] ||
                          city.city;

                        // Count sites and stops
                        const hasSite = city.site && city.site.trim() !== '';
                        const stopCount = city.stops.length;
                        const siteCount = hasSite ? 1 : 0;
                        const totalLocations = siteCount + stopCount;

                        return (
                          <div key={city.id} className="flex items-center">
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                              {index + 1}. {cityLabel}
                              {hasSite && <span className="text-green-600"> • {city.site}</span>}
                              {totalLocations > 0 && (
                                <span className="text-green-600"> ({totalLocations})</span>
                              )}
                            </span>
                            {index < formData.routeCities.filter((c) => c.city).length - 1 && (
                              <span className="mx-1 text-green-600">
                                {i18n.language === 'ar' ? '←' : '→'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}



                {/* Pricing */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">{t('shipments.shipment_price')}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">{t('common.price')} (SAR)</label>
                      <input
                        type="text"
                        name="shipmentPrice"
                        value={formData.shipmentPrice === 0 ? '' : formData.shipmentPrice}
                        onChange={(e) => handleNumberInput(e.target.value, (value) => setFormData(prev => ({ ...prev, shipmentPrice: value })))}
                        className="input-field"
                        placeholder="0.00"
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                    <div>
                      <label className="label">{t('shipments.vat_amount')} (15%)</label>
                      <div className="flex items-center space-x-3 rtl:space-x-reverse mt-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="isVatIncluded"
                            checked={formData.isVatIncluded}
                            onChange={() => setFormData(prev => ({ ...prev, isVatIncluded: true }))}
                            className="mr-2 rtl:ml-2 rtl:mr-0"
                          />
                          {t('shipments.vat_included')}
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="isVatIncluded"
                            checked={!formData.isVatIncluded}
                            onChange={() => setFormData(prev => ({ ...prev, isVatIncluded: false }))}
                            className="mr-2 rtl:ml-2 rtl:mr-0"
                          />
                          {t('shipments.vat_excluded')}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Backhaul Button */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={toggleBackhaul}
                      className={`flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.showBackhaul
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-50'
                        }`}
                    >
                      <TruckIcon className="w-4 h-4" />
                      <span>{t('shipments.backhaul', i18n.language === 'ar' ? 'حمولة عودة' : 'Backhaul')}</span>
                    </button>
                  </div>

                  {/* Backhaul Fields */}
                  {formData.showBackhaul && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Description field - First for Arabic (RTL), Second for English (LTR) */}
                        <div className={i18n.language === 'ar' ? 'order-1' : 'order-2'}>
                          <label className="label">{t('shipments.backhaul_description', i18n.language === 'ar' ? 'الوصف' : t('common.description'))}</label>
                          <input
                            type="text"
                            value={formData.backhaulDescription}
                            onChange={(e) => setFormData(prev => ({ ...prev, backhaulDescription: e.target.value }))}
                            className="input-field"
                            placeholder={t('shipments.backhaul_description', i18n.language === 'ar' ? 'الوصف' : t('common.description'))}
                          />
                        </div>
                        {/* Price field - Second for Arabic (RTL), First for English (LTR) */}
                        <div className={i18n.language === 'ar' ? 'order-2' : 'order-1'}>
                          <label className="label">{t('shipments.backhaul_price', i18n.language === 'ar' ? 'سعر الحمولة العائدة' : 'Backhaul Price')} (SAR)</label>
                          <input
                            type="text"
                            value={formData.backhaulPrice === 0 ? '' : formData.backhaulPrice}
                            onChange={(e) => handleNumberInput(e.target.value, (value) => setFormData(prev => ({ ...prev, backhaulPrice: value })))}
                            className="input-field"
                            placeholder="0.00"
                            onFocus={(e) => e.target.select()}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Itemized Costs */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-blue-900">
                      {i18n.language === 'ar' ? 'التكاليف المفصلة' : t('itemized_costs.itemized_costs', 'Itemized Costs')}
                    </h4>
                    <button
                      type="button"
                      onClick={addCostItem}
                      className="flex items-center space-x-1 rtl:space-x-reverse text-blue-600 hover:text-blue-800 bg-blue-100 hover:bg-blue-200 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>{i18n.language === 'ar' ? 'إضافة بند تكلفة' : t('itemized_costs.add_cost_item', 'Add Cost Item')}</span>
                    </button>
                  </div>

                  <div className="text-xs text-blue-700 mb-3">
                    {i18n.language === 'ar' ? 'أضف التكاليف الإضافية مثل المبيت، عمالة التحميل والتفريغ، المحطات، إلخ. الأسعار بدون ضريبة القيمة المضافة.' : t('itemized_costs.itemized_costs_help', 'Add additional costs like overnight stay, loading/unloading labor, stops, etc. Prices are without VAT.')}
                  </div>

                  {formData.itemizedCosts.length > 0 ? (
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="grid grid-cols-12 gap-2 text-xs font-medium text-blue-800 pb-2 border-b border-blue-200">
                        <div className="col-span-3">{t('common.description')}</div>
                        <div className="col-span-1 text-center">{t('common.quantity')}</div>
                        <div className="col-span-2 text-center">{t('itemized_costs.unit_price')}</div>
                        <div className="col-span-2 text-center">{t('common.total')}</div>
                        <div className="col-span-2 text-center">{t('itemized_costs.vat_amount')}</div>
                        <div className="col-span-1 text-center">{t('itemized_costs.total_with_vat')}</div>
                        <div className="col-span-1 text-center">{t('common.actions')}</div>
                      </div>

                      {/* Cost Items */}
                      {formData.itemizedCosts.map((item) => {
                        const vatAmount = item.total * 0.15;
                        const totalWithVat = item.total + vatAmount;

                        return (
                          <div key={item.id} className="grid grid-cols-12 gap-2 items-center bg-white p-3 rounded-lg border border-blue-200">
                            <div className="col-span-3">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => updateCostItem(item.id, 'description', e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={t('itemized_costs.cost_description_placeholder', 'e.g., Overnight stay, Loading labor, Stop fee')}
                              />
                            </div>
                            <div className="col-span-1">
                              <input
                                type="text"
                                value={item.quantity === 0 ? '' : item.quantity}
                                onChange={(e) => handleNumberInput(e.target.value, (value) => updateCostItem(item.id, 'quantity', value))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-center"
                                placeholder="1"
                                onFocus={(e) => e.target.select()}
                              />
                            </div>
                            <div className="col-span-2">
                              <input
                                type="text"
                                value={item.unitPrice === 0 ? '' : item.unitPrice}
                                onChange={(e) => handleNumberInput(e.target.value, (value) => updateCostItem(item.id, 'unitPrice', value))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-center"
                                placeholder="0.00"
                                onFocus={(e) => e.target.select()}
                              />
                            </div>
                            <div className="col-span-2">
                              <div className="text-sm font-medium text-center text-gray-700 bg-gray-50 py-1.5 px-1 rounded border">
                                {item.total.toFixed(2)}
                              </div>
                            </div>
                            <div className="col-span-2">
                              <div className="text-sm font-medium text-center text-orange-600 bg-orange-50 py-1.5 px-1 rounded border">
                                {vatAmount.toFixed(2)}
                              </div>
                            </div>
                            <div className="col-span-1">
                              <div className="text-sm font-medium text-center text-green-600 bg-green-50 py-1.5 px-1 rounded border">
                                {totalWithVat.toFixed(2)}
                              </div>
                            </div>
                            <div className="col-span-1 text-center">
                              <button
                                type="button"
                                onClick={() => removeCostItem(item.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                                title={t('common.remove')}
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Itemized Total */}
                      <div className="pt-2 border-t border-blue-200 space-y-1">
                        <div className="flex justify-between text-sm text-blue-900">
                          <span>{t('itemized_costs.itemized_subtotal')}:</span>
                          <span>{t('common.currency')} {calculateItemizedTotal().toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-orange-600">
                          <span>{t('itemized_costs.itemized_vat')}:</span>
                          <span>{t('common.currency')} {(calculateItemizedTotal() * 0.15).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm font-semibold text-green-600 border-t border-blue-200 pt-1">
                          <span>{t('itemized_costs.itemized_total_with_vat')}:</span>
                          <span>{t('common.currency')} {(calculateItemizedTotal() * 1.15).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-blue-600">
                      <DocumentTextIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{i18n.language === 'ar' ? 'لم يتم إضافة تكاليف مفصلة بعد' : t('itemized_costs.no_itemized_costs', 'No itemized costs added yet')}</p>
                      <p className="text-xs text-blue-500 mt-1">{i18n.language === 'ar' ? 'انقر على "إضافة بند تكلفة" لبدء إضافة التكاليف التفصيلية' : t('itemized_costs.click_add_to_start', 'Click "Add Cost Item" to start adding detailed costs')}</p>
                    </div>
                  )}

                  {/* Quick Add Buttons */}
                  <div className="mt-4 pt-3 border-t border-blue-200">
                    <div className={`flex items-center mb-2 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                      <div className="text-xs font-medium text-blue-800 flex-1">{i18n.language === 'ar' ? 'إضافة سريعة للبنود الشائعة' : t('itemized_costs.quick_add', 'Quick Add Common Items')}:</div>
                      <button
                        type="button"
                        onClick={() => setShowQuickItemsModal(true)}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1 rounded transition-colors"
                        title={t('itemized_costs.manage_quick_items', 'Manage Quick Items')}
                      >
                        <CogIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {quickItems.map((quickItem) => (
                        <button
                          key={quickItem.id}
                          type="button"
                          onClick={() => {
                            const newId = nextCostItemId.current++;
                            const unitPrice = quickItem.isVatIncluded ? quickItem.price / 1.15 : quickItem.price;
                            setFormData(prev => ({
                              ...prev,
                              itemizedCosts: [
                                ...prev.itemizedCosts,
                                {
                                  id: newId,
                                  description: i18n.language === 'ar' ? quickItem.name : quickItem.nameEn,
                                  quantity: 1,
                                  unitPrice: unitPrice,
                                  total: unitPrice
                                }
                              ]
                            }));
                          }}
                          className="text-xs bg-white hover:bg-blue-50 text-blue-700 border border-blue-300 px-2 py-1 rounded transition-colors"
                        >
                          {i18n.language === 'ar' ? quickItem.name : quickItem.nameEn}
                          {quickItem.price > 0 && (
                            <span className="text-green-600 ml-1">
                              ({quickItem.price.toFixed(2)} {t('common.currency')})
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">
                      {t('shipments.pickup_date_time') + ' *'}
                    </label>
                    <DateTimeField
                      value={formData.pickupDate}
                      onChange={(e) => {
                        const newPickupDate = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          pickupDate: newPickupDate
                        }));
                      }}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="label">{t('shipments.expected_delivery')}</label>
                    <DateTimeField
                      value={formData.deliveryDate}
                      onChange={handleInputChange}
                      className="input-field"
                    />
                  </div>
                </div>


              </div>
            </div>

            {/* Vehicle & Driver */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold flex items-center">
                  <TruckIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                  {t('shipments.vehicle_driver_assignment')}
                </h3>
              </div>
              <div className="card-body space-y-4">
                {/* External Driver Checkbox */}
                <div className="flex items-center space-x-3 rtl:space-x-reverse p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <input
                    type="checkbox"
                    id="useExternalDriver"
                    checked={formData.useExternalDriver}
                    onChange={handleExternalDriverToggle}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="useExternalDriver" className="text-sm font-medium text-blue-900">
                    {t('drivers.use_external_driver', 'Use External Driver')}
                  </label>
                  <span className="text-xs text-blue-700">
                    {t('drivers.subcontractors_note', '(For subcontractors or temporary drivers)')}
                  </span>
                </div>

                {/* Driver Selection - Regular or External */}
                {formData.useExternalDriver ? (
                  // External Driver Fields
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    {externalDriversList.length > 0 && (() => {
                      const sorted = [...externalDriversList].sort((a, b) => {
                        const ua = externalDriverUsage.get(a.id) || { count: 0, lastUsedMs: 0 };
                        const ub = externalDriverUsage.get(b.id) || { count: 0, lastUsedMs: 0 };
                        if (ub.count !== ua.count) return ub.count - ua.count;
                        if (ub.lastUsedMs !== ua.lastUsedMs) return ub.lastUsedMs - ua.lastUsedMs;
                        return (a.name || '').localeCompare(b.name || '');
                      });
                      return (
                        <div>
                          <label className="label">
                            {t('drivers.choose_registered_driver', 'Choose registered driver')}
                          </label>
                          <select
                            value={formData.externalDriverId || ''}
                            onChange={(e) => {
                              const driverId = e.target.value;
                              if (!driverId) {
                                setFormData(prev => ({
                                  ...prev,
                                  externalDriverId: '',
                                  externalDriverName: '',
                                  externalDriverPhone: ''
                                }));
                                return;
                              }
                              const d = externalDriversList.find(x => x.id === driverId);
                              if (d) {
                                setFormData(prev => ({
                                  ...prev,
                                  externalDriverId: d.id,
                                  externalDriverName: d.name || '',
                                  externalDriverPhone: d.phone || ''
                                }));
                              }
                            }}
                            className="input-field"
                          >
                            <option value="">
                              {t('drivers.select_or_add_new', 'Select existing or enter new below')}
                            </option>
                            {sorted.map(d => {
                              const u = externalDriverUsage.get(d.id);
                              const meta = u && u.count > 0
                                ? ` — ${u.count} ${t('drivers.trips', 'trips')}`
                                : '';
                              return (
                                <option key={d.id} value={d.id}>
                                  {d.name}{d.phone ? ` (${d.phone})` : ''}{meta}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      );
                    })()}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">{t('drivers.external_driver_name', 'External Driver Name')}</label>
                      <input
                        type="text"
                        name="externalDriverName"
                        value={formData.externalDriverName}
                        onChange={handleInputChange}
                        className="input-field"
                        placeholder={t('drivers.driver_name_placeholder', 'Enter driver name')}
                      />
                    </div>
                    <div>
                      <label className="label">{t('drivers.external_driver_phone', 'External Driver Phone')}</label>
                      <input
                        type="tel"
                        name="externalDriverPhone"
                        value={formData.externalDriverPhone}
                        onChange={handleExternalDriverPhoneChange}
                        className="input-field"
                        placeholder={t('drivers.driver_phone_placeholder', 'Enter phone number')}
                      />
                      {formData.externalDriverId && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Driver information auto-filled from previous records
                        </p>
                      )}
                    </div>
                  </div>
                  </div>
                ) : (
                  // Regular Driver and Vehicle Selection - Side by side
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">{t('shipments.assigned_driver')}</label>
                      <DriverSelect
                        driversList={drivers}
                        busyMap={driverBusyMap}
                        value={formData.driverId}
                        onChange={(driverId) =>
                          handleInputChange({ target: { name: 'driverId', value: driverId } })
                        }
                        disabled={loadingDrivers}
                        placeholder={loadingDrivers ? 'Loading drivers…' : t('shipments.select_driver')}
                      />
                    </div>
                    <div>
                      <label className="label">{t('shipments.assigned_vehicle')}</label>
                      <select
                        name="vehicleId"
                        value={formData.vehicleId}
                        onChange={handleInputChange}
                        className="input-field"
                        disabled={loadingVehicles}
                      >
                        <option value="">
                          {loadingVehicles ? 'Loading vehicles...' :
                            vehicles.length === 0 ? 'No vehicles available' :
                              t('shipments.select_vehicle')}
                        </option>
                        {vehicles.map(vehicle => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.plateNumber} - {getTruckTypeName(vehicle.truckType || vehicle.type)} {vehicle.capacity && `(${vehicle.capacity})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Vehicle/Truck Type Selection for External Drivers */}
                {formData.useExternalDriver && (
                  <div>
                    <label className="label">{t('vehicles.type', 'Truck Type')}</label>
                    <select
                      name="truckType"
                      value={formData.truckType || ''}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      <option value="">{t('vehicles.select_type', 'Select Type')}</option>
                      {truckTypes.map(truckType => (
                        <option key={truckType.value} value={truckType.value}>
                          {truckType.name[i18n.language] || truckType.name.en}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Cargo Details */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold flex items-center">
                  <DocumentTextIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                  {t('shipments.cargo_details')}
                </h3>
              </div>
              <div className="card-body space-y-4">
                <div>
                  <label className="label">{t('shipments.cargo_description_optional')}</label>
                  <textarea
                    name="cargoDescription"
                    value={formData.cargoDescription}
                    onChange={handleInputChange}
                    className="input-field"
                    rows={3}
                    placeholder={t('shipments.describe_cargo_optional')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t('shipments.weight')} ({t('units.kg')})</label>
                    <input
                      type="text"
                      name="cargoWeight"
                      value={formData.cargoWeight === 0 ? '' : formData.cargoWeight}
                      onChange={(e) => handleNumberInput(e.target.value, (value) => setFormData(prev => ({ ...prev, cargoWeight: value })))}
                      className="input-field"
                      placeholder="0.00"
                      onFocus={(e) => e.target.select()}
                    />
                  </div>
                  <div>
                    <label className="label">{t('shipments.declared_value')} ({t('common.currency')})</label>
                    <input
                      type="text"
                      name="cargoValue"
                      value={formData.cargoValue === 0 ? '' : formData.cargoValue}
                      onChange={(e) => handleNumberInput(e.target.value, (value) => setFormData(prev => ({ ...prev, cargoValue: value })))}
                      className="input-field"
                      placeholder="0.00"
                      onFocus={(e) => e.target.select()}
                    />
                  </div>
                </div>

                <div>
                  <label className="label">{t('shipments.notes')}</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="input-field"
                    rows={2}
                    placeholder={t('shipments.any_additional_notes')}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Summary */}
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">{t('shipments.shipment_summary')}</h3>
              </div>
              <div className="card-body space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('shipments.client')}:</span>
                  <span className="font-medium">
                    {formData.clientId ?
                      clients.find(c => c.id === formData.clientId)?.companyName || 'Unknown' :
                      t('shipments.not_selected')
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('shipments.service_type')}:</span>
                  <span className="font-medium">
                    {(() => {
                      const serviceType = serviceTypes.find(st => st.value === formData.serviceType);
                      if (serviceType) {
                        return serviceType.name?.[i18n.language] || serviceType.name?.en || t(`service_${formData.serviceType}`, formData.serviceType);
                      }
                      return t(`service_${formData.serviceType}`, formData.serviceType);
                    })()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('shipments.priority')}:</span>
                  <span className={`font-medium ${formData.priority === 'urgent' ? 'text-red-600' :
                    formData.priority === 'high' ? 'text-orange-600' :
                      formData.priority === 'normal' ? 'text-blue-600' : 'text-gray-600'
                    }`}>
                    {t(`shipments.priority_${formData.priority}`)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('shipments.driver')}:</span>
                  <span className="font-medium">
                    {formData.useExternalDriver ?
                      (formData.externalDriverName || t('shipments.not_assigned')) :
                      (formData.driverId ? drivers.find(d => d.id === formData.driverId)?.name : t('shipments.not_assigned'))
                    }
                    {formData.useExternalDriver && (
                      <span className="text-xs text-blue-600 block">
                        ({t('drivers.external_driver', 'External Driver')})
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('shipments.vehicle')}:</span>
                  <span className="font-medium">
                    {formData.vehicleId ? vehicles.find(v => v.id === formData.vehicleId)?.plateNumber : t('shipments.not_assigned')}
                  </span>
                </div>
                {formData.cargoValue && (
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="text-gray-600">{t('shipments.declared_value')}:</span>
                                          <span className="font-medium text-green-600">{t('common.currency')} {formData.cargoValue}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold flex items-center">
                  <CurrencyDollarIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                  {t('shipments.estimated_cost')}
                </h3>
              </div>
              <div className="card-body">
                <div className="space-y-2 text-sm">
                  {formData.shipmentPrice || formData.backhaulPrice || formData.itemizedCosts.length > 0 ? (
                    <>
                      {(() => {
                        const enteredPrice = parseFloat(formData.shipmentPrice || 0);
                        const backhaulPrice = parseFloat(formData.backhaulPrice || 0);
                        const itemizedTotal = calculateItemizedTotal();
                        const combinedBasePrice = enteredPrice + backhaulPrice + itemizedTotal;

                        let basePrice, vatAmount, totalPrice;

                        if (formData.isVatIncluded && enteredPrice > 0) {
                          // Main price includes VAT, backhaul and itemized costs are without VAT
                          const mainBasePrice = enteredPrice / 1.15;
                          basePrice = mainBasePrice + backhaulPrice + itemizedTotal;
                          vatAmount = (enteredPrice - mainBasePrice) + (backhaulPrice * 0.15) + (itemizedTotal * 0.15);
                          totalPrice = enteredPrice + backhaulPrice + (backhaulPrice * 0.15) + itemizedTotal + (itemizedTotal * 0.15);
                        } else {
                          // Main price, backhaul, and itemized costs are all without VAT
                          basePrice = combinedBasePrice;
                          vatAmount = combinedBasePrice * 0.15;
                          totalPrice = combinedBasePrice + vatAmount;
                        }

                        return (
                          <>
                            {enteredPrice > 0 && (
                              <div className="flex justify-between">
                                <span>{t('itemized_costs.main_shipment_price')}:</span>
                                <span>SAR {enteredPrice.toFixed(2)}</span>
                              </div>
                            )}
                            {backhaulPrice > 0 && (
                              <div className="flex justify-between">
                                <span>{t('shipments.backhaul_price', i18n.language === 'ar' ? 'سعر الحمولة العائدة' : 'Backhaul Price')}:</span>
                                <span>SAR {backhaulPrice.toFixed(2)}</span>
                              </div>
                            )}
                            {itemizedTotal > 0 && (
                              <div className="flex justify-between">
                                <span>{t('itemized_costs.itemized_costs_total')}:</span>
                                <span>SAR {itemizedTotal.toFixed(2)}</span>
                              </div>
                            )}
                            {(enteredPrice > 0 || backhaulPrice > 0 || itemizedTotal > 0) && (
                              <>
                                <div className="flex justify-between border-t pt-2">
                                  <span>{t('itemized_costs.subtotal_before_vat')}:</span>
                                  <span>SAR {basePrice.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>{t('shipments.vat_amount')} (15%):</span>
                                  <span>SAR {vatAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between font-semibold text-lg text-green-600 border-t pt-2">
                                  <span>{t('shipments.total_price')}:</span>
                                  <span>SAR {totalPrice.toFixed(2)}</span>
                                </div>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <div className="text-gray-500 text-center py-4">
                      {t('shipments.enter_price_for_breakdown')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                type="submit"
                disabled={loading || clients.length === 0}
                className="btn btn-primary w-full"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner />
                    <span className="ml-2">{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  </div>
                ) : (
                  isEditMode ? t('shipments.update_shipment', 'Update Shipment') : t('shipments.create_shipment')
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/shipments')}
                className="btn btn-secondary w-full"
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Quick Items Management Modal */}
      {showQuickItemsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('itemized_costs.manage_quick_items', 'Manage Quick Items')}</h3>
              <button
                type="button"
                onClick={() => {
                  setShowQuickItemsModal(false);
                  setEditingQuickItem(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Quick Items List */}
            <div className="space-y-3 mb-4">
              {quickItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{item.nameEn} / {item.name}</div>
                    <div className="text-xs text-gray-600">
                      Price: {item.price.toFixed(2)} SAR {item.isVatIncluded ? '(VAT Included)' : '(VAT Excluded)'}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setEditingQuickItem(item)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      {t('common.edit', t('common.edit'))}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteQuickItem(item.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      {t('common.delete', t('common.delete'))}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add/Edit Form */}
            <QuickItemForm
              item={editingQuickItem}
              onSave={saveQuickItem}
              onCancel={() => setEditingQuickItem(null)}
              t={t}
            />

            <div className="flex justify-end pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowQuickItemsModal(false);
                  setEditingQuickItem(null);
                }}
                className="btn btn-secondary"
              >
                {t('common.close', t('common.close'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Client Modal */}
      {showQuickClientModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Quick Add Client</h3>
              <button
                type="button"
                onClick={() => setShowQuickClientModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleQuickClientSubmit} className="space-y-4">
              <div>
                <label className="label">Company Name *</label>
                <input
                  type="text"
                  name="companyName"
                  value={quickClientData.companyName}
                  onChange={handleQuickClientInputChange}
                  className="input-field"
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div>
                <label className="label">Contact Person</label>
                <input
                  type="text"
                  name="contactPerson"
                  value={quickClientData.contactPerson}
                  onChange={handleQuickClientInputChange}
                  className="input-field"
                  placeholder="Contact person name (optional)"
                />
              </div>

              <div>
                <label className="label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={quickClientData.phone}
                  onChange={handleQuickClientInputChange}
                  className="input-field"
                  placeholder="Phone number (optional)"
                />
              </div>

              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={quickClientData.email}
                  onChange={handleQuickClientInputChange}
                  className="input-field"
                  placeholder="Email address (optional)"
                />
              </div>

              <div>
                <label className="label">Address</label>
                <textarea
                  name="address"
                  value={quickClientData.address}
                  onChange={handleQuickClientInputChange}
                  className="input-field"
                  rows="2"
                  placeholder="Business address (optional)"
                />
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> This is a quick client creation. You can complete the full information later in the Clients section.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowQuickClientModal(false)}
                  className="btn btn-secondary"
                  disabled={quickClientLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={quickClientLoading}
                >
                  {quickClientLoading ? (
                    <div className="flex items-center">
                      <LoadingSpinner />
                      <span className="ml-2">Adding...</span>
                    </div>
                  ) : (
                    <>
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Add Client
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Service Types Management Modal */}
      <ServiceTypesModal
        isOpen={showServiceTypesModal}
        onClose={() => setShowServiceTypesModal(false)}
        onSuccess={() => {
          // Reload service types after successful update
          const fetchServiceTypes = async () => {
            if (!currentCompany?.id) return;
            try {
              const serviceTypesData = await serviceTypesService.getServiceTypes(currentCompany.id);
              setServiceTypes(serviceTypesData);
            } catch (error) {
              console.error('Error fetching service types:', error);
            }
          };
          fetchServiceTypes();
        }}
      />
    </div>
  );
};

export default CreateShipment; 